import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Text,
  Spinner,
  Tooltip,
  makeStyles,
} from '@fluentui/react-components';
import {
  Play20Regular,
  Add20Regular,
  LinkMultiple20Regular,
  Sparkle20Regular,
} from '@fluentui/react-icons';
import { useDataModels } from '../contexts/DataModelsContext';
import { useTopBarTitle, useTopBarActions, useTopBarBreadcrumbs } from '../contexts/TopBarContext';
import { dataModelsService } from '../api/dataModelsService';
import type { JsonFieldPromotionOverrides } from '../api/dataModelsService';
import { datasetDisplayName } from '../utils/datasetDisplayName';
import { semanticViewDisplayNames } from '../utils/semanticViewDisplayNames';
import { useJobProgress } from '../contexts/WorkspaceEventsContext';

import SchemaCanvas from '../components/data-model/SchemaCanvas';
import SemanticViewsPanel from '../components/data-model/SemanticViewsPanel';
import BottomDrawer from '../components/data-model/BottomDrawer';
import AiAssistantPlaceholder from '../components/data-model/AiAssistantPlaceholder';
import type { PhysicalTable, PhysicalColumn, SchemaRelationship, SelectionType } from '../components/data-model/types';
import { getPhysicalTablesForView } from '../components/data-model/getPhysicalTablesForView';
import { MutationButton, useWorkspaceAccess } from '../components/security/WorkspaceAccess';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  body: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  center: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  canvasArea: {
    flex: 1,
    overflow: 'hidden',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: '16px',
  },
});

/**
 * Parse schemaJson from a dataset into PhysicalColumn[].
 * Schema uses: name, dtype, pkish (0-1), is_id_like, is_primary_key, distinct_count
 */
function parseColumns(schemaJson: unknown, relationshipColumnNames?: Set<string>): PhysicalColumn[] {
  // Handle case where schemaJson is a JSON string instead of parsed array
  let schema = schemaJson;
  if (typeof schema === 'string') {
    try { schema = JSON.parse(schema); } catch { return []; }
  }
  if (!schema || !Array.isArray(schema)) return [];

  const columns = (schema as Array<Record<string, unknown>>).map((col) => {
    const name = (col.name as string) || (col.column_name as string) || '';
    const rawType = (col.inferred_type as string) || (col.dtype as string)
      || (col.type as string) || (col.data_type as string) || '';
    // Simplify dtype for display (e.g. "datetime64[us]" -> "datetime64", "Int64" -> "int64")
    const type = rawType
      .replace(/\[.*\]/, '')
      .replace(/^Int(\d+)$/, 'int$1')
      .toLowerCase() || 'unknown';
    // PK detection: explicit flag or pkish >= 0.95
    const pkish = typeof col.pkish === 'number' ? col.pkish : 0;
    const isPrimaryKey = !!(col.isPrimaryKey || col.is_primary_key || col.pk || pkish >= 0.95);
    // FK detection: is_id_like but not PK, or explicit flag
    const isIdLike = !!(col.is_id_like || col.isIdLike);
    const isForeignKey = !!(col.isForeignKey || col.is_foreign_key || col.fk || (isIdLike && !isPrimaryKey && pkish > 0.3));
    const isRelationshipColumn = relationshipColumnNames?.has(name) ?? false;
    return { name, type, isPrimaryKey, isForeignKey, isRelationshipColumn };
  });

  // Sort: PK first, then FK/relationship columns, then the rest
  columns.sort((a, b) => {
    const rank = (c: PhysicalColumn) => c.isPrimaryKey ? 0 : (c.isForeignKey || c.isRelationshipColumn) ? 1 : 2;
    return rank(a) - rank(b);
  });

  return columns;
}

/** Extract row_count from profile_json if available on dataset */
function extractRowCount(dataset: Record<string, unknown>): number | null {
  const profile = dataset.profile as Record<string, unknown> | undefined;
  if (profile?.profileJson && typeof profile.profileJson === 'object') {
    const pj = profile.profileJson as Record<string, unknown>;
    if (typeof pj.row_count === 'number') return pj.row_count;
    if (typeof pj.row_count === 'string') return parseInt(pj.row_count, 10) || null;
  }
  return null;
}

function extractJsonPromotions(dataset: Record<string, unknown>) {
  const profile = dataset.profile as { profileJson?: Record<string, unknown> } | null | undefined;
  const value = profile?.profileJson?.json_field_promotions;
  if (!value || typeof value !== 'object') return { candidates: [], recommendations: [] };
  const record = value as Record<string, unknown>;
  return {
    candidates: Array.isArray(record.candidates) ? record.candidates : [],
    recommendations: Array.isArray(record.promotions) ? record.promotions : [],
  };
}

const DataModelDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    currentDataModel,
    loadDataModel,
    refreshCurrentDataModel,
    buildJobIds,
    setBuildJobId,
    updateDataModelStatusLocally,
  } = useDataModels();
  const { runMutation, isReadOnly } = useWorkspaceAccess();
  const styles = useStyles();

  const [loadFailed, setLoadFailed] = useState(false);
  const [selection, setSelection] = useState<SelectionType>(null);
  const [autoSelectedFirstView, setAutoSelectedFirstView] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showAiPanel, setShowAiPanel] = useState(false);

  // Build progress via workspace SSE (replaces polling)
  const activeJobId = id ? buildJobIds[id] || null : null;
  const jobProgress = useJobProgress(activeJobId);
  const hasRefreshedRef = useRef(false);
  const isBuilding = jobProgress?.state === 'running' || jobProgress?.state === 'pending';
  const buildCompleted = jobProgress?.state === 'completed';
  const buildCancelled = jobProgress?.state === 'cancelled';

  useEffect(() => {
    if (buildCompleted && !hasRefreshedRef.current) {
      hasRefreshedRef.current = true;
      refreshCurrentDataModel();
      if (id) {
        updateDataModelStatusLocally(id, 'active');
        setBuildJobId(id, null);
      }
    }
  }, [buildCompleted, refreshCurrentDataModel, id, setBuildJobId, updateDataModelStatusLocally]);

  useEffect(() => {
    if (buildCancelled && id) {
      refreshCurrentDataModel();
      setBuildJobId(id, null);
    }
  }, [buildCancelled, refreshCurrentDataModel, id, setBuildJobId]);

  useEffect(() => {
    if (isBuilding) hasRefreshedRef.current = false;
  }, [isBuilding]);

  useTopBarTitle('Semantic Studio');
  useTopBarBreadcrumbs([
    { text: 'Data Models', to: '/data-models' },
    { text: currentDataModel?.name ?? '...', to: `/data-models/${id}` },
    { text: 'Semantic Studio' },
  ]);

  useEffect(() => {
    if (id) {
      setLoadFailed(false);
      setAutoSelectedFirstView(false);
      loadDataModel(id).catch(() => setLoadFailed(true));
    }
  }, [id, loadDataModel]);

  // Auto-select view: prefer viewId from query param, otherwise first view
  useEffect(() => {
    if (currentDataModel && currentDataModel.views.length > 0 && !autoSelectedFirstView) {
      const targetViewId = searchParams.get('viewId');
      const matchedView = targetViewId
        ? currentDataModel.views.find((v) => v.id === targetViewId)
        : null;
      setSelection({ kind: 'view', id: matchedView ? matchedView.id : currentDataModel.views[0].id });
      setAutoSelectedFirstView(true);
    }
  }, [currentDataModel, autoSelectedFirstView, searchParams]);

  const handleBuild = useCallback(() => {
    if (!id) return;
    return runMutation(async () => {
      try {
        const { jobId } = await dataModelsService.build(id, { maxViews: 6 });
        setBuildJobId(id, jobId);
        updateDataModelStatusLocally(id, 'building');
      } catch (err) {
        console.error('Build failed:', err);
      }
    });
  }, [id, runMutation, setBuildJobId, updateDataModelStatusLocally]);

  // Top bar actions
  const topBarActions = useMemo(() => (
    <>
      <Tooltip content="Coming soon" relationship="description">
        <Button appearance="subtle" icon={<Add20Regular />} disabled>+ New View</Button>
      </Tooltip>
      <Tooltip content="Coming soon" relationship="description">
        <Button appearance="subtle" icon={<LinkMultiple20Regular />} disabled>+ Add Relation</Button>
      </Tooltip>
      <Tooltip content="Coming soon" relationship="description">
        <Button appearance="subtle" icon={<Sparkle20Regular />} disabled>AI Generate</Button>
      </Tooltip>
      <MutationButton
        appearance="primary"
        icon={isBuilding ? <Spinner size="tiny" /> : <Play20Regular />}
        onClick={handleBuild}
        disabled={isBuilding}
      >
        {isBuilding ? 'Building...' : 'Build Semantic Layer'}
      </MutationButton>
    </>
  ), [handleBuild, isBuilding]);
  useTopBarActions(topBarActions);

  // Build set of relationship columns per dataset for highlighting
  const relationshipColumnsByDataset = useMemo(() => {
    if (!currentDataModel) return new Map<string, Set<string>>();
    const map = new Map<string, Set<string>>();
    // Map datasetId -> data_model_dataset id
    const datasetIdToTableId = new Map<string, string>();
    for (const d of currentDataModel.datasets) {
      datasetIdToTableId.set(d.datasetId, d.id);
    }
    for (const r of currentDataModel.relationships) {
      const fromId = datasetIdToTableId.get(r.fromDatasetId) || r.fromDatasetId;
      const toId = datasetIdToTableId.get(r.toDatasetId) || r.toDatasetId;
      if (!map.has(fromId)) map.set(fromId, new Set());
      if (!map.has(toId)) map.set(toId, new Set());
      map.get(fromId)!.add(r.fromColumn);
      map.get(toId)!.add(r.toColumn);
    }
    return map;
  }, [currentDataModel]);

  // Derive physical tables from datasets
  const physicalTables: PhysicalTable[] = useMemo(() => {
    if (!currentDataModel) return [];
    return currentDataModel.datasets.map((d) => ({
      id: d.id,
      datasetId: d.datasetId,
      name: d.dataset.name,
      displayName: datasetDisplayName(d.dataset.name, d.dataset.schemaJson),
      aliasName: d.aliasName,
      connectionName: d.dataset.connection.name,
      columns: parseColumns(d.dataset.schemaJson, relationshipColumnsByDataset.get(d.id)),
      rowCount: extractRowCount(d.dataset as unknown as Record<string, unknown>),
      primaryTimeColumnOverride: d.dataset.primaryTimeColumnOverride,
      jsonFieldPromotionCandidates: extractJsonPromotions(d.dataset as unknown as Record<string, unknown>).candidates,
      jsonFieldPromotionRecommendations: extractJsonPromotions(d.dataset as unknown as Record<string, unknown>).recommendations,
      jsonFieldPromotionOverrides: d.dataset.jsonFieldPromotionOverrides,
    }));
  }, [currentDataModel, relationshipColumnsByDataset]);

  const displayViews = useMemo(
    () => semanticViewDisplayNames(currentDataModel?.views || [], physicalTables),
    [currentDataModel, physicalTables],
  );

  // Derive relationships
  const schemaRelationships: SchemaRelationship[] = useMemo(() => {
    if (!currentDataModel) return [];
    // Map datasetId -> data_model_dataset id
    const datasetIdToTableId = new Map<string, string>();
    for (const d of currentDataModel.datasets) {
      datasetIdToTableId.set(d.datasetId, d.id);
    }
    const labels = new Map(physicalTables.map(table => [table.datasetId, table.aliasName || table.displayName || table.name]));
    return currentDataModel.relationships.map((r) => ({
      id: r.id,
      fromTableId: datasetIdToTableId.get(r.fromDatasetId) || r.fromDatasetId,
      fromColumn: r.fromColumn,
      toTableId: datasetIdToTableId.get(r.toDatasetId) || r.toDatasetId,
      toColumn: r.toColumn,
      joinType: r.joinType,
      relationshipType: r.relationshipType,
      confidence: r.confidence,
      status: r.status,
      fromTableName: labels.get(r.fromDatasetId) || r.fromDataset.name,
      toTableName: labels.get(r.toDatasetId) || r.toDataset.name,
    }));
  }, [currentDataModel, physicalTables]);

  // Compute visible table IDs based on selection (filter mode, not highlight)
  const visibleTableIds: Set<string> = useMemo(() => {
    if (!selection) return new Set(physicalTables.map((t) => t.id));
    if (selection.kind === 'view') {
      const view = currentDataModel?.views.find((v) => v.id === selection.id);
      if (!view) return new Set(physicalTables.map((t) => t.id));
      return getPhysicalTablesForView(view, currentDataModel!.views, physicalTables);
    }
    if (selection.kind === 'table') {
      const connected = new Set<string>();
      connected.add(selection.id);
      for (const r of schemaRelationships) {
        if (r.fromTableId === selection.id) connected.add(r.toTableId);
        if (r.toTableId === selection.id) connected.add(r.fromTableId);
      }
      return connected;
    }
    return new Set(physicalTables.map((t) => t.id));
  }, [selection, schemaRelationships, currentDataModel, physicalTables]);

  // Filtered tables and relationships for the canvas
  const filteredTables = useMemo(
    () => physicalTables.filter((t) => visibleTableIds.has(t.id)),
    [physicalTables, visibleTableIds]
  );
  const filteredRelationships = useMemo(
    () => schemaRelationships.filter((r) => visibleTableIds.has(r.fromTableId) && visibleTableIds.has(r.toTableId)),
    [schemaRelationships, visibleTableIds]
  );

  const selectedTableId = selection?.kind === 'table' ? selection.id : null;
  // Empty highlighted set — we filter instead of highlighting now
  const highlightedTableIds = useMemo(() => new Set<string>(), []);

  // Info banner for schema canvas
  const infoBanner = useMemo(() => {
    if (selection?.kind === 'view') {
      const view = displayViews.find((v) => v.id === selection.id);
      if (!view) return null;
      const dj = view.descriptionJson;
      const rawQuestions = dj?.business_questions;
      const questionsStr = Array.isArray(rawQuestions)
        ? rawQuestions.join(' · ')
        : rawQuestions || undefined;
      return {
        title: view.title || view.name,
        viewType: view.viewType,
        description: dj?.summary || view.description || undefined,
        businessQuestions: questionsStr,
        whyImportant: dj?.why_important || undefined,
      };
    }
    return null;
  }, [selection, displayViews]);

  const handleTableSelect = useCallback((tableId: string | null) => {
    if (tableId) {
      setSelection({ kind: 'table', id: tableId });
    } else {
      setSelection(null);
    }
  }, []);

  const handleTableDoubleClick = useCallback((tableId: string) => {
    setSelection({ kind: 'table', id: tableId });
    setDrawerOpen(true);
  }, []);

  const handleViewSelect = useCallback((viewId: string) => {
    setSelection((prev) =>
      prev?.kind === 'view' && prev.id === viewId ? null : { kind: 'view', id: viewId }
    );
  }, []);

  const handlePrimaryTimeColumnOverride = useCallback(async (
    datasetId: string,
    override: string | null,
  ) => {
    if (!id) return;
    const { jobId } = await dataModelsService.updateDatasetPrimaryTimeColumn(id, datasetId, override);
    await refreshCurrentDataModel();
    setBuildJobId(id, jobId);
    updateDataModelStatusLocally(id, 'building');
  }, [id, refreshCurrentDataModel, setBuildJobId, updateDataModelStatusLocally]);

  const handleJsonFieldPromotionOverrides = useCallback(async (
    datasetId: string, overrides: JsonFieldPromotionOverrides,
  ) => {
    if (!id) return;
    const { jobId } = await dataModelsService.updateDatasetJsonFieldPromotions(id, datasetId, overrides);
    await refreshCurrentDataModel();
    setBuildJobId(id, jobId);
    updateDataModelStatusLocally(id, 'building');
  }, [id, refreshCurrentDataModel, setBuildJobId, updateDataModelStatusLocally]);

  // Loading / error states
  if (!currentDataModel) {
    if (loadFailed) {
      return (
        <div className={styles.errorContainer}>
          <Text>Data model not found.</Text>
          <Button onClick={() => navigate('/data-models')}>Back to Data Models</Button>
        </div>
      );
    }
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="medium" />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.body}>
        <AiAssistantPlaceholder collapsed={!showAiPanel} onToggle={() => setShowAiPanel((v) => !v)} />

        <div className={styles.center}>
          <div className={styles.canvasArea}>
            <SchemaCanvas
              tables={filteredTables}
              relationships={filteredRelationships}
              selectedTableId={selectedTableId}
              highlightedTableIds={highlightedTableIds}
              onTableSelect={handleTableSelect}
              onTableDoubleClick={handleTableDoubleClick}
              infoBanner={infoBanner}
            />
          </div>

          <BottomDrawer
            selection={selection}
            views={displayViews}
            tables={physicalTables}
            isOpen={drawerOpen}
            onToggle={() => setDrawerOpen((o) => !o)}
            onPrimaryTimeColumnOverride={handlePrimaryTimeColumnOverride}
            onJsonFieldPromotionOverrides={handleJsonFieldPromotionOverrides}
            isReadOnly={isReadOnly}
          />
        </div>

        <SemanticViewsPanel
          views={displayViews}
          selectedViewId={selection?.kind === 'view' ? selection.id : null}
          onViewSelect={handleViewSelect}
        />
      </div>
    </div>
  );
};

export default DataModelDetailPage;